/**
 * What Narcis sells and at what price, as code — the counterpart of `PROPOSITION.md` in this
 * directory. Surfaces import from here and restate nothing: no price and no fixed sentence of the
 * canon is typed anywhere else.
 *
 * What a buyer is charged is the Stripe price, not this constant: its amount is set in terraform
 * (`deployment/environments/prod/eu_central_1/stripe.tf`, `local.stripe_narcis_catalog`) and its
 * id reaches the checkout as `NARCIS_MODEL_PRICE_ID`. The price's base is the dollar.
 */
import {CURRENCY_SYMBOL, type DisplayCurrency} from '@core/billing/displayCurrency'

/** The object sold. */
export const MODEL_NAME = 'your model'

/** One payment, one-time, in the base currency's minor unit. */
export const MODEL_PRICE = {amountCents: 4900, currency: 'USD'} as const

/**
 * The amount in each currency, minor unit: the dollar base and the price's fixed `currency_options`
 * in `stripe.tf`, which Checkout charges a buyer of that currency; third currencies are converted
 * from the dollar at Checkout. The two lists are the same price: change them together.
 */
export const MODEL_AMOUNTS: Record<DisplayCurrency, number> = {usd: MODEL_PRICE.amountCents, eur: 4900, inr: 149900}

/** The currencies the price carries as fixed options, for `displayCurrency`. */
export const MODEL_CURRENCIES: readonly DisplayCurrency[] = ['eur', 'inr']

/** `€49`, `$49` or `₹1,499` — the price as the pages write it. */
export const priceLabel = (currency: DisplayCurrency = 'usd') =>
    `${CURRENCY_SYMBOL[currency]}${(MODEL_AMOUNTS[currency] / 100).toLocaleString('en-US')}`

/** The upload step's price line (canon § 8). */
export const priceLine = (currency: DisplayCurrency = 'usd') => `One payment of ${priceLabel(currency)}.`

/** The pack page's line under the title (canon § 8). */
export const packLine = (currency: DisplayCurrency = 'usd') =>
    `This pack — nine photos of you — and every other pack on Narcis. ${priceLabel(currency)}, one-time payment.`

/**
 * The verbs of the one path (PROPOSITION.md § 7): no button carries a price but the one that pays
 * (`startButton`). The pack page's main button and coda ask for the pack's nine; its image button asks for one.
 * The wall's verb for a visitor with no model names the next action instead, whatever the intent.
 */
export const PHOTOS_VERB = 'Get these 9 photos of me'
export const PHOTO_VERB = 'Get this photo of me'

/** The intake page: its heading, the shortened guarantee its line carries, and the button that leaves it. */
export const INTAKE_HEADING = 'Add 6 photos of you'
/** The wall's verb on an armed tile for a visitor with no model, single photo or pack: the next action. */
export const WALL_ADD_PHOTOS_VERB = `${INTAKE_HEADING} →`
export const GUARANTEE_SHORT = 'Not happy? We refund you.'
export const INTAKE_CONTINUE = 'Continue'

/** The same verb once the payment is made and the model not yet created: nothing more is asked. */
export const CREATE_VERB_PAID = 'Create my model →'

/** The intake's Start when it opens the payment (canon § 8, "The start"). */
export const startButton = (currency: DisplayCurrency = 'usd') => `Pay ${priceLabel(currency)} and create my model`

/** The same Start on the retrain intake: new photos make a new model, a new purchase (canon § 2). */
export const retrainStartButton = (currency: DisplayCurrency = 'usd') => `Pay ${priceLabel(currency)} and create my new model`

// The list price in the base currency, the dollar, for the surfaces no viewer's country reads: meta, OG cards,
// structured data, the terms and the home's indexed copy.
export const PRICE_LABEL = priceLabel('usd')
export const PRICE_LINE = priceLine()
export const PACK_LINE = packLine()
export const START_BUTTON = startButton()
export const RETRAIN_START_BUTTON = retrainStartButton()

/** Stated wherever the price is asked for (canon § 3). */
export const GUARANTEE = 'Not happy with your photos? We refund you.'

/** The intake page's line under its heading: the photos asked for, then the price and the guarantee as text. */
export const intakeLine = (currency: DisplayCurrency = 'usd') =>
    `At least 6 clear photos of your face. ${priceLine(currency)} ${GUARANTEE_SHORT}`

/**
 * The feature page as a checkout step, on the path through the wall (PROPOSITION.md § 7): its progress, the summary
 * beside the cards, the bar the phone keeps at the bottom, and the way back. The price and the guarantee are the
 * canon's own (`priceLine`, `GUARANTEE`); nothing here restates them.
 */
export const STEP_PHOTOS_LABEL = 'Your photos'
export const STEP_PHOTOS_CHANGE = 'Change'
export const STEP_OFFER = 'Your model, every pack'
/** The summary's line before the guarantee, which the page links to the refund terms. */
export const STEP_SUMMARY_LEAD = 'One payment. New packs all year.'
export const STEP_BACK = 'Back to your search'
export const STEP_CARD_MORE = 'See examples'
export const STEP_TO_TOP = 'Back to top'
/** The bar's line before the shortened guarantee, which the page links to the refund terms. */
export const stepBarLead = (currency: DisplayCurrency = 'usd') => `One payment of ${priceLabel(currency)} ·`
/** The count of photos given, as the step and its photo strip say it. */
export const stepPhotoCount = (count: number) => `${count} ${count === 1 ? 'photo' : 'photos'}`

/** Where the guarantee leads: the terms' refund clause (§ 5.4). */
export const REFUND_TERMS_HREF = '/terms#tos-5'

/** When results come (the claims ledger, "Delivery time"; the arrival mail is the "we email you"). */
export const READY_LINE = 'Your model is ready in a few hours. We email you.'

/** The sign-in card's words on the step: why sign in, then pay. */
export const STEP_SIGN_IN_COPY = {
    title: 'Sign in to pay',
    subtitle: 'Your photos are kept while you sign in. Then you pay, and we create your model.',
} as const
/** The step's line when the visitor comes back from the sign-in. */
export const STEP_SIGNED_IN = 'Signed in. One step left.'

/** After the payment, once, on the wall the visitor lands on. */
export const PAYMENT_RECEIVED_NOTICE = 'Payment received. We are creating your model.'
/** The wall's chip while the model is created, and the verb on an armed tile once the photo is asked for. */
export const WALL_CREATING = 'creating your model'
export const WALL_PHOTO_ASKED = 'On its way'

/** The canon block (PROPOSITION.md § 1): the thesis and the six properties, one sentence each. */
export const CATALOGUE_LINE = 'Narcis is a catalogue of 35,000 photographs waiting for your face.'
export const SPAN_LINE =
    'A dating profile, a startup founder in San Francisco, a trip to Paris, your wedding, a Chanel tweed, a yakuza: every one art-directed and photographic.'
export const GROWING_LINE = 'New packs come out all year, by season and event, like issues of a magazine.'
export const NOTHING_TO_WRITE_LINE = 'There is nothing to write: you pick a pack, and it is ready in seconds.'
export const OUTCOME_LINE = 'Every photo looks like you, well photographed.'
export const EDIT_LINE = 'If one is nearly right, say what to change, in your own words.'
export const TERMS_LINE = `One payment of ${PRICE_LABEL} for the whole catalogue, including everything added after.`
export const CANON_BLOCK = [CATALOGUE_LINE, SPAN_LINE, GROWING_LINE, NOTHING_TO_WRITE_LINE, OUTCOME_LINE, EDIT_LINE, TERMS_LINE].join(' ')

/** The OG card and `og:description`: the object and the price (canon § 7). */
export const OG_LINE = `Photos of you in this pack and every other pack on Narcis. ${PRICE_LINE}`

/** The OG image's bottom label. */
export const OG_LABEL = `Your model · ${PRICE_LABEL}`

/**
 * How it works, three steps — the pack page's visible section and its `HowTo` JSON-LD. No price:
 * the steps read true for a buyer who has already paid.
 */
export const HOW_IT_WORKS: ReadonlyArray<{title: string; body: string}> = [
    {title: 'Add your photos', body: 'Add photos of yourself. We check each one when you add it.'},
    {title: 'We create your model', body: 'We create your AI model from your photos. Your photos are ready in a few hours.'},
    {title: 'Every pack, with your face', body: 'This pack and every other pack on Narcis can then be made with your face, and single photos too.'},
]

/** The pricing page's statement of the object (canon § 8, "Pricing page"), before the price. */
export const PRICING_SUMMARY =
    `${CATALOGUE_LINE} Add your photos once; we create your model from them, and every pack on Narcis, nine photos of you in one setting, and any single photo can then be made with your face.`

/** The hub's line for a visitor with no model: the object, then the price while it is asked. */
export const HUB_LINE = 'Create your model once, and every pack here is made with your face.'

/** What the price is not (canon § 2). */
export const PRICE_TERMS = 'One-time payment. No subscription, no credits.'

/** The sr-only opening of a pack page, after the pack's own name. */
export const PACK_SUMMARY = 'Nine photographs of you in one setting: one pack of the Narcis catalogue.'

/** The sr-only headings of a pack page, the same on every pack. No price. */
export const PACK_HEADINGS: readonly string[] = [
    'Nine photos of you in one setting',
    'How your model is created from your photos',
    'Every pack on Narcis, made with your face',
]

/**
 * The site's description: the home meta and lede, and the default meta of every other page. The canon block
 * (PROPOSITION.md § 1) in full: the thesis, then the six properties, each sentence able to stand alone when a
 * snippet or a crawler quotes it.
 */
export const SITE_DESCRIPTION = CANON_BLOCK

/**
 * The questions every pack page answers the same way, asked after the pack's own (PROPOSITION.md § 1: a pack
 * page speaks the pack; the template speaks the catalogue, the process and the price). Sources: the photo
 * bounds are the intake's; the processing clause is the terms' § 10.1.
 */
export const PACK_FAQ: ReadonlyArray<{q: string; a: string}> = [
    {q: 'Will the photos look like me?', a: `${OUTCOME_LINE} ${EDIT_LINE}`},
    {q: 'Do I have to write a prompt?', a: `No. ${NOTHING_TO_WRITE_LINE}`},
    {q: 'How many photos of myself do I add?', a: 'Between six and twenty, from different days and in different light, with your face clear. We check each one when you add it.'},
    {q: 'What happens to the photos I add?', a: 'They are used only to create your model, on our own servers in the European Union. They are not shared and not sold.'},
]

/** The address a refund is asked at. */
export const CONTACT_EMAIL = 'contact@narcis.ai'

/** How the guarantee is honoured (canon § 3), as the terms and the FAQ state it. */
export const REFUND_HOW = `Write to ${CONTACT_EMAIL} within 14 days of your first photos and we refund the whole payment. We ask nothing beyond the request.`

const CANON_TOKENS: Record<string, string> = {
    PRICE_LABEL, PRICE_LINE, GUARANTEE, REFUND_HOW, CONTACT_EMAIL,
    CATALOGUE_LINE, SPAN_LINE, GROWING_LINE, NOTHING_TO_WRITE_LINE, OUTCOME_LINE, EDIT_LINE, TERMS_LINE,
}

/**
 * Fills the `{TOKEN}`s of copy kept as data (the home's SEO blocks and FAQ), so that copy states
 * the price and the guarantee without typing them. `extra` carries values known only at render
 * (`PACK_COUNT`). An unknown token throws: a misspelt one would otherwise ship as text.
 */
export function withCanon(text: string, extra: Record<string, string> = {}): string {
    return text.replace(/\{([A-Z_]+)\}/g, (_, key: string) => {
        const value = extra[key] ?? CANON_TOKENS[key]
        if (value === undefined) throw new Error(`withCanon: unknown token {${key}}`)
        return value
    })
}
